import React from "react";
import { Skeleton, Empty } from "antd";
import dayjs from "dayjs";
import "./DashboardContent.scss";
import CallTypeDoughnutCard from "../CallTypeDoughnutCard";
import PerformanceChartCard from "../PerformanceChartCard";
import TicketTypeCard from "../TicketTypeCard";
import Avatar from "../avatar";

const DashboardContent = ({
  analytics,
  selectedPeriod,
  onPeriodChange,
  isLoading,
}: any) => {
  const stats = [
    {
      title: "Unassigned tickets",
      value: analytics?.tickets?.[0]?.unassigned_tickets_count ?? 0,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M14.7681 4.21094C15.8179 5.07928 16.6116 6.21684 17.0644 7.50177"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.23151 4.21094C4.18166 5.07924 3.38783 6.21681 2.93506 7.50177"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.0058 2.77904C10.6946 2.40645 9.30557 2.40645 7.99438 2.77904"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.333 10.4163H9.58301V5.83301"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Ongoing tickets",
      value: analytics?.tickets?.[0]?.ongoing_tickets_count ?? 0,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M15.8362 4.16211H10.0004C6.31696 4.16211 3.33095 7.14812 3.33095 10.8316C3.33095 14.515 6.31696 17.501 10.0004 17.501C12.6459 17.501 14.9317 15.9607 16.0098 13.728"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.3351 1.66211L15.8362 4.16315"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.3354 6.66315L15.8364 4.16211"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Resolved tickets",
      value: analytics?.tickets?.[0]?.resolved_tickets_count ?? 0,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect
            x="2.5"
            y="2.5"
            width="15"
            height="15"
            rx="4.6875"
            stroke="#10100F"
            strokeWidth="1.5"
          />
          <path
            d="M8.4725 6.61084L7.08333 8.00001L6.25 7.16667"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.25 7.1665H13.75"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.4725 12L7.08333 13.3892L6.25 12.5558"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.25 12.5557H13.75"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Closed tickets",
      value: analytics?.tickets?.[0]?.closed_tickets_count ?? 0,
      icon: (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M16.6667 5.4165L7.50004 14.5832L3.33337 10.4165"
            stroke="#10100F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  const today = dayjs().format("/ dddd, MMMM D");
  const firstName =
    JSON.parse(localStorage.getItem("Data") || "{}")?.data?.member_first_name ||
    "User";

  const hasTickets = analytics?.tickets && analytics?.tickets?.length > 0;

  return (
    <div className="dashboard-content h-full p-6">
      {/* Greeting */}
      <div className="user-greeting-section flex gap-3 items-center">
        <Avatar color="#76B0BA" size="large" name={firstName} />
        <div className="flex gap-1">
          <p className="user_name mb-0">Hi, {firstName}</p>
          <p className="mb-0 today_date">{today}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="stats-row">
        {isLoading ? (
          [...Array(4)].map((_, i) => (
            <div className="stat-card" key={i}>
              <Skeleton.Input active style={{ width: 40, height: 40 }} />
              <Skeleton.Input
                active
                style={{ width: 80, height: 20, marginTop: 8 }}
              />
            </div>
          ))
        ) : hasTickets ? (
          stats.map((stat, index) => (
            <div className="stat-card" key={index}>
              <div className="stat-icon">{stat.icon}</div>
              <div className="stat-content flex gap-2 justify-center">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-title">{stat.title}</div>
              </div>
            </div>
          ))
        ) : (
          <Empty description="No Dashboard Data" />
        )}
      </div>

      {/* Charts */}
      <div className="dashboard-cards-row flex gap-3 w-full">
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div className="dashboard-card" key={i}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          ))
        ) : hasTickets ? (
          <>
            <div className="dashboard-card">
              <TicketTypeCard tickets={analytics?.tickets[0] || {}} />
            </div>
            <div className="dashboard-card">
              <PerformanceChartCard
                performance={analytics?.performance}
                selectedPeriod={selectedPeriod}
                onPeriodChange={onPeriodChange}
              />
            </div>
            <div className="dashboard-card doughnut-chart-wrapper w-1/4">
              <CallTypeDoughnutCard callTypes={analytics?.callTypes} />
            </div>
          </>
        ) : (
          <Empty
            className="w-full flex justify-center"
            description="No Charts available"
          />
        )}
      </div>
    </div>
  );
};

export default DashboardContent;
